import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuthState } from '@/hooks/useAuth'
import { getUserSavedItems } from '@/lib/savesApi'

interface UserSaves {
  saves: string[]
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

const SavesContext = createContext<UserSaves | undefined>(undefined)

interface SavesProviderProps {
  children: React.ReactNode
}

export function SavesProvider({ children }: SavesProviderProps) {
  const { user } = useAuthState()
  const [saves, setSaves] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchSaves = async () => {
    if (!user) {
      setSaves([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const userSaves = await getUserSavedItems(user.uid)
      setSaves(userSaves)
    } catch (err) {
      console.error('[SavesContext] Error fetching user saves:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch user saves'))
    } finally {
      setLoading(false)
    }
  }

  // Fetch saves when user changes
  useEffect(() => {
    fetchSaves()
  }, [user])

  const value = {
    saves,
    loading,
    error,
    refresh: fetchSaves
  }

  return (
    <SavesContext.Provider value={value}>
      {children}
    </SavesContext.Provider>
  )
}

export function useSaves(): UserSaves {
  const ctx = useContext(SavesContext)
  if (!ctx) {
    throw new Error('useSaves must be used within a SavesProvider')
  }
  return ctx
} 